import { PhotoIcon } from '@heroicons/react/24/outline';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useMediaPreview } from './useMediaPreview';
import AstroImage from '@/components/ui/custom/AstroImage';
import { detectMediaKind } from '@/utils/media';

type AnswerMediaPreviewProps = {
  mediaFile?: File | string | null;
  mediaLink?: string | null;
  altText?: string;
  placeholder?: string;
};

export default function AnswerMediaPreview({
  mediaFile,
  mediaLink,
  altText,
  placeholder,
}: AnswerMediaPreviewProps) {
  const mediaPreview = useMediaPreview(mediaFile, mediaLink);
  const kind = detectMediaKind(mediaFile) ?? detectMediaKind(mediaPreview) ?? 'image';

  return (
    <ConditionalRenderer
      condition={!!mediaPreview}
      fallback={
        <div className="flex items-center gap-2 text-base-content">
          <PhotoIcon className="size-6" />
          <span className="text-sm">{placeholder}</span>
        </div>
      }
    >
      <div className="flex flex-col gap-2 flex-1 items-start w-full">
        <ConditionalRenderer condition={kind === 'image'}>
          <AstroImage
            src={mediaPreview ?? undefined}
            alt={altText ?? ''}
            zoomable
            className="h-23 w-23 rounded object-cover"
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={kind === 'audio'}>
          <audio
            src={mediaPreview ?? undefined}
            controls
            preload="metadata"
            className="w-full"
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={kind === 'video'}>
          <video
            src={mediaPreview ?? undefined}
            controls
            preload="metadata"
            className="h-32 w-full rounded object-contain"
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={!!altText}>
          <span className="text-sm text-base-content">{altText}</span>
        </ConditionalRenderer>
      </div>
    </ConditionalRenderer>
  );
}
